import { logger } from './index';

export class CorrelationManager {
  private static instance: CorrelationManager;
  private currentCorrelationId: string | null = null;

  private constructor() {}

  public static getInstance(): CorrelationManager {
    if (!CorrelationManager.instance) {
      CorrelationManager.instance = new CorrelationManager();
    }
    return CorrelationManager.instance;
  }

  public generateCorrelationId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public setCorrelationId(id: string | null): void {
    this.currentCorrelationId = id;
    logger.setCorrelationId(id);
  }

  public getCorrelationId(): string | null {
    return this.currentCorrelationId;
  }

  public withCorrelationId<T>(id: string, fn: () => T): T {
    const previousId = this.currentCorrelationId;
    this.setCorrelationId(id);

    try {
      return fn();
    } finally {
      this.setCorrelationId(previousId);
    }
  }

  public async withCorrelationIdAsync<T>(
    id: string,
    fn: () => Promise<T>
  ): Promise<T> {
    const previousId = this.currentCorrelationId;
    this.setCorrelationId(id);

    try {
      return await fn();
    } finally {
      this.setCorrelationId(previousId);
    }
  }

  public setupFetchInterceptor(): void {
    const originalFetch = window.fetch;

    window.fetch = async (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> => {
      const correlationId =
        this.currentCorrelationId || this.generateCorrelationId();

      const headers = new Headers(init?.headers);
      headers.set('X-Correlation-ID', correlationId);

      const modifiedInit = {
        ...init,
        headers,
      };

      logger.debug('API Request', {
        url: input.toString(),
        method: modifiedInit.method || 'GET',
        correlationId,
        headers: Object.fromEntries(headers.entries()),
      });

      const startTime = performance.now();

      try {
        const response = await originalFetch(input, modifiedInit);

        const duration = performance.now() - startTime;

        logger.info('API Response', {
          url: input.toString(),
          method: modifiedInit.method || 'GET',
          status: response.status,
          statusText: response.statusText,
          duration: Math.round(duration),
          correlationId,
        });

        return response;
      } catch (error) {
        const duration = performance.now() - startTime;

        // Don't log errors for the internal /_logs endpoint
        const url = input.toString();
        if (!url.includes('/_logs')) {
          logger.error('API Request Failed', {
            url,
            method: modifiedInit.method || 'GET',
            duration: Math.round(duration),
            correlationId,
            error: error instanceof Error ? error.message : String(error),
          });
        }

        throw error;
      }
    };
  }

  public setupAxiosInterceptor(): void {
    // For future axios integration if needed
    // This would require checking if axios is available
    logger.debug('Axios interceptor setup - not implemented yet');
  }
}

export const correlationManager = CorrelationManager.getInstance();
